import { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../components/admin/AdminLayout';
import PageHeader from '../components/admin/PageHeader';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { getApiErrorMessage } from '../utils/apiError';
import { Plus, Trash2, Users, Search, Send, X, Calendar, UserPlus } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INP = 'w-full px-3 py-2 rounded-xl border-2 border-[var(--border-light)] bg-white text-sm font-semibold focus:border-[var(--brand-primary)] focus:outline-none';
const LBL = 'text-[10px] font-bold text-[var(--text-muted)] mb-1 block';

export default function AdminExamGroups() {
  const { lang } = useLanguage();
  const { authHeaders } = useAuth();
  const toast = useToast();
  const tr = (t, e) => (lang === 'tr' ? t : e);

  const [groups, setGroups] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ ad: '', course_id: '', sinav_tarihi: '' });
  const [saving, setSaving] = useState(false);

  const [sel, setSel] = useState(null); // selected group detail
  const [msg, setMsg] = useState(null); // {title, message, channels} when message form open
  const [sending, setSending] = useState(false);
  const [stuSearch, setStuSearch] = useState('');
  const [stuResults, setStuResults] = useState([]);
  const [stuPicked, setStuPicked] = useState([]);
  const [searching, setSearching] = useState(false);

  const fetchGroups = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/exam-groups`, { headers: authHeaders() });
      setGroups(r.data.groups || []);
    } catch (err) { toast.error(getApiErrorMessage(err, lang)); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchGroups(); axios.get(`${API}/courses?limit=100`).then((r) => setCourses(r.data.courses || [])).catch(() => {}); }, []); // eslint-disable-line

  const openGroup = async (id) => {
    setMsg(null); setStuSearch(''); setStuResults([]); setStuPicked([]);
    try {
      const r = await axios.get(`${API}/admin/exam-groups/${id}`, { headers: authHeaders() });
      setSel(r.data);
    } catch (err) { toast.error(getApiErrorMessage(err, lang)); }
  };

  const createGroup = async () => {
    if (!form.ad.trim()) { toast.error(tr('Grup adı zorunlu', 'Name required')); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/exam-groups`, form, { headers: authHeaders() });
      setForm({ ad: '', course_id: '', sinav_tarihi: '' });
      fetchGroups();
      toast.success(tr('Grup oluşturuldu', 'Group created'));
    } catch (err) { toast.error(getApiErrorMessage(err, lang)); }
    finally { setSaving(false); }
  };

  const deleteGroup = async (id) => {
    if (!window.confirm(tr('Grup silinsin mi?', 'Delete group?'))) return;
    try {
      await axios.delete(`${API}/admin/exam-groups/${id}`, { headers: authHeaders() });
      if (sel && sel.group_id === id) setSel(null);
      fetchGroups();
    } catch (err) { toast.error(getApiErrorMessage(err, lang)); }
  };

  const saveDate = async (sinav_tarihi) => {
    try {
      await axios.put(`${API}/admin/exam-groups/${sel.group_id}`, { sinav_tarihi }, { headers: authHeaders() });
      setSel((p) => ({ ...p, sinav_tarihi }));
      fetchGroups();
    } catch (err) { toast.error(getApiErrorMessage(err, lang)); }
  };

  const searchStudents = async () => {
    setSearching(true);
    try {
      const r = await axios.get(`${API}/admin/student-ops?search=${encodeURIComponent(stuSearch)}&page_size=20`, { headers: authHeaders() });
      const existing = new Set((sel.members || []).map((m) => m.user_id));
      setStuResults((r.data.students || []).filter((s) => !existing.has(s.user_id)));
    } catch (err) { toast.error(getApiErrorMessage(err, lang)); }
    finally { setSearching(false); }
  };

  const addMembers = async () => {
    if (!stuPicked.length) return;
    try {
      const r = await axios.post(`${API}/admin/exam-groups/${sel.group_id}/members`, { user_ids: stuPicked }, { headers: authHeaders() });
      setSel(r.data); setStuPicked([]); setStuResults([]); setStuSearch('');
      fetchGroups();
      toast.success(tr('Öğrenciler eklendi', 'Students added'));
    } catch (err) { toast.error(getApiErrorMessage(err, lang)); }
  };

  const removeMember = async (uid) => {
    try {
      await axios.delete(`${API}/admin/exam-groups/${sel.group_id}/members/${uid}`, { headers: authHeaders() });
      setSel((p) => ({ ...p, members: (p.members || []).filter((m) => m.user_id !== uid), member_count: (p.member_count || 1) - 1 }));
      fetchGroups();
    } catch (err) { toast.error(getApiErrorMessage(err, lang)); }
  };

  const openMessage = () => {
    const dateStr = sel.sinav_tarihi ? String(sel.sinav_tarihi).slice(0, 10) : '';
    setMsg({
      title: tr('Sınav Tarihi Bilgilendirmesi', 'Exam Date Notice'),
      message: tr(`Değerli öğrencimiz, sınav tarihiniz: ${dateStr}. Lütfen hazırlıklı olunuz.`, `Dear student, your exam date is: ${dateStr}.`),
      channels: ['in_app'],
    });
  };

  const toggleChannel = (ch) => setMsg((m) => ({ ...m, channels: m.channels.includes(ch) ? m.channels.filter((c) => c !== ch) : [...m.channels, ch] }));

  const sendMessage = async () => {
    if (!sel || !(sel.members || []).length) { toast.error(tr('Grupta öğrenci yok', 'No members')); return; }
    if (!msg.title.trim() || !msg.message.trim()) { toast.error(tr('Başlık ve mesaj gerekli', 'Title and message required')); return; }
    setSending(true);
    try {
      await axios.post(`${API}/admin/bulk/notify`, {
        user_ids: sel.members.map((m) => m.user_id),
        title: msg.title.trim(), message: msg.message.trim(), channels: msg.channels,
      }, { headers: authHeaders() });
      toast.success(tr(`${sel.members.length} öğrenciye gönderildi`, `Sent to ${sel.members.length}`));
      setMsg(null);
    } catch (err) { toast.error(getApiErrorMessage(err, lang)); }
    finally { setSending(false); }
  };

  return (
    <AdminLayout>
      <PageHeader
        breadcrumbs={[{ label: 'Admin', href: '/admin' }, { label: tr('Sınav Grupları', 'Exam Groups') }]}
        title={tr('Sınav Grupları', 'Exam Groups')}
        subtitle={tr('MEB sınavı için öğrenci grupları oluştur, yönet ve toplu mesaj gönder', 'Create exam groups, manage members and send bulk messages')}
      />

      {/* Yeni grup */}
      <div className="card-clay p-4 mb-5">
        <h3 className="font-bold text-sm mb-3">{tr('Yeni Grup', 'New Group')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div className="md:col-span-1">
            <label className={LBL}>{tr('Grup Adı', 'Group Name')}</label>
            <input value={form.ad} onChange={(e) => setForm({ ...form, ad: e.target.value })} className={INP} placeholder={tr('Örn. Haziran Grubu', 'e.g. June Group')} />
          </div>
          <div>
            <label className={LBL}>{tr('Eğitim', 'Course')}</label>
            <select value={form.course_id} onChange={(e) => setForm({ ...form, course_id: e.target.value })} className={INP}>
              <option value="">{tr('Seçiniz', 'Select')}</option>
              {courses.map((c) => <option key={c.course_id} value={c.course_id}>{c.title}</option>)}
            </select>
          </div>
          <div>
            <label className={LBL}>{tr('Sınav Tarihi', 'Exam Date')}</label>
            <input type="date" value={form.sinav_tarihi} onChange={(e) => setForm({ ...form, sinav_tarihi: e.target.value })} className={INP} />
          </div>
          <button onClick={createGroup} disabled={saving} className="flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-green-500 text-white text-sm font-bold border-2 border-green-600 hover:bg-green-600 disabled:opacity-50">
            <Plus size={16} /> {saving ? '...' : tr('Oluştur', 'Create')}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Grup listesi */}
        <div className="card-clay overflow-hidden">
          <div className="p-3 border-b border-[var(--border-light)] font-bold text-sm">{tr('Gruplar', 'Groups')} ({groups.length})</div>
          {loading && <div className="p-4 text-center text-sm text-[var(--text-muted)]">{tr('Yükleniyor...', 'Loading...')}</div>}
          {!loading && groups.length === 0 && <div className="p-6 text-center text-sm text-[var(--text-muted)]">{tr('Henüz grup yok', 'No groups')}</div>}
          {groups.map((g) => (
            <div key={g.group_id} onClick={() => openGroup(g.group_id)}
              className={`flex items-center gap-3 px-4 py-3 border-t border-[var(--border-light)] first:border-t-0 cursor-pointer hover:bg-gray-50 ${sel && sel.group_id === g.group_id ? 'bg-blue-50' : ''}`}>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm truncate">{g.ad}</p>
                <p className="text-[11px] text-[var(--text-muted)] truncate">{g.course_title || tr('Eğitim yok', 'No course')}</p>
              </div>
              {g.sinav_tarihi && <span className="text-[11px] font-bold text-[var(--brand-primary)] flex items-center gap-1"><Calendar size={12} />{String(g.sinav_tarihi).slice(0, 10)}</span>}
              <span className="text-[11px] font-bold text-[var(--text-muted)] flex items-center gap-1"><Users size={12} />{g.member_count}</span>
              <button onClick={(e) => { e.stopPropagation(); deleteGroup(g.group_id); }} className="p-1.5 text-red-500 hover:bg-red-50 rounded"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>

        {/* Grup detayi */}
        <div className="card-clay p-4">
          {!sel ? (
            <div className="text-center text-sm text-[var(--text-muted)] py-10">{tr('Detay için bir grup seçin', 'Select a group')}</div>
          ) : (
            <>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-bold text-base">{sel.ad}</h3>
                  <p className="text-xs text-[var(--text-muted)]">{sel.course_title}</p>
                </div>
                <button onClick={openMessage} className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-500 text-white text-xs font-bold hover:bg-blue-600">
                  <Send size={14} /> {tr('Sınav Mesajı Gönder', 'Send Exam Message')}
                </button>
              </div>

              <div className="flex items-center gap-2 mb-3">
                <label className="text-xs font-bold text-[var(--text-muted)]">{tr('Sınav Tarihi', 'Exam Date')}:</label>
                <input type="date" value={sel.sinav_tarihi ? String(sel.sinav_tarihi).slice(0, 10) : ''} onChange={(e) => saveDate(e.target.value)}
                  className="px-2 py-1 rounded-lg border-2 border-[var(--border-light)] text-xs font-semibold" />
                <span className="ml-auto text-xs font-bold text-[var(--text-muted)] flex items-center gap-1"><Users size={13} /> {sel.member_count || 0}</span>
              </div>

              {/* Mesaj formu */}
              {msg && (
                <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-3 mb-3">
                  <input value={msg.title} onChange={(e) => setMsg({ ...msg, title: e.target.value })} placeholder={tr('Başlık', 'Title')} className="w-full px-3 py-2 mb-2 rounded-lg border-2 border-[var(--border-light)] text-sm font-semibold" />
                  <textarea value={msg.message} onChange={(e) => setMsg({ ...msg, message: e.target.value })} rows={3} className="w-full px-3 py-2 mb-2 rounded-lg border-2 border-[var(--border-light)] text-sm" />
                  <div className="flex items-center gap-3 mb-2 text-xs font-semibold">
                    <label className="flex items-center gap-1 text-[var(--text-muted)]"><input type="checkbox" checked readOnly className="rounded" /> {tr('Uygulama içi', 'In-app')}</label>
                    <label className="flex items-center gap-1 cursor-pointer"><input type="checkbox" checked={msg.channels.includes('email')} onChange={() => toggleChannel('email')} className="rounded" /> E-posta</label>
                    <label className="flex items-center gap-1 cursor-pointer"><input type="checkbox" checked={msg.channels.includes('whatsapp')} onChange={() => toggleChannel('whatsapp')} className="rounded" /> WhatsApp</label>
                  </div>
                  <div className="flex justify-end gap-2">
                    <button onClick={() => setMsg(null)} className="px-3 py-1.5 rounded-lg text-xs font-bold hover:bg-white">{tr('İptal', 'Cancel')}</button>
                    <button onClick={sendMessage} disabled={sending} className="px-4 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-bold hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1"><Send size={13} /> {sending ? '...' : tr('Gönder', 'Send')}</button>
                  </div>
                </div>
              )}

              {/* Ogrenci ekle */}
              <div className="border-2 border-[var(--border-light)] rounded-xl p-3 mb-3">
                <label className={LBL}>{tr('Öğrenci Ekle', 'Add Students')}</label>
                <div className="flex gap-2 mb-2">
                  <div className="relative flex-1">
                    <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
                    <input value={stuSearch} onChange={(e) => setStuSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && searchStudents()} placeholder={tr('Ara (ad, e-posta, TC)...', 'Search...')} className="w-full pl-8 pr-3 py-1.5 rounded-lg border-2 border-[var(--border-light)] text-sm" />
                  </div>
                  <button onClick={searchStudents} disabled={searching} className="px-3 py-1.5 rounded-lg bg-gray-100 text-xs font-bold hover:bg-gray-200">{searching ? '...' : tr('Ara', 'Search')}</button>
                  {stuPicked.length > 0 && <button onClick={addMembers} className="px-3 py-1.5 rounded-lg bg-green-500 text-white text-xs font-bold hover:bg-green-600 flex items-center gap-1"><UserPlus size={13} /> {tr(`Ekle (${stuPicked.length})`, `Add (${stuPicked.length})`)}</button>}
                </div>
                {stuResults.length > 0 && (
                  <div className="max-h-40 overflow-y-auto space-y-1">
                    {stuResults.map((s) => (
                      <label key={s.user_id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-gray-50 cursor-pointer text-sm">
                        <input type="checkbox" checked={stuPicked.includes(s.user_id)} onChange={() => setStuPicked((p) => p.includes(s.user_id) ? p.filter((x) => x !== s.user_id) : [...p, s.user_id])} className="rounded" />
                        <span className="font-semibold">{s.name}</span>
                        <span className="text-[11px] text-[var(--text-muted)]">{s.email}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Uyeler */}
              <div className="space-y-1 max-h-72 overflow-y-auto">
                {(sel.members || []).length === 0 && <p className="text-xs text-[var(--text-muted)] text-center py-4">{tr('Grupta öğrenci yok', 'No members yet')}</p>}
                {(sel.members || []).map((m) => (
                  <div key={m.user_id} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-[var(--border-light)] text-sm">
                    <div className="flex-1 min-w-0"><span className="font-semibold">{m.name}</span> <span className="text-[11px] text-[var(--text-muted)]">{m.email}</span></div>
                    {m.phone && <span className="text-[11px] text-[var(--text-muted)]">{m.phone}</span>}
                    <button onClick={() => removeMember(m.user_id)} className="p-1 text-red-500 hover:bg-red-50 rounded"><X size={14} /></button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
